import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { api } from '../lib/api';

interface AuthedImageProps {
  photoId: number;
  alt: string;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
  /** Wenn true: bereits eager laden (z.B. im Lightbox-Modal). */
  eager?: boolean;
  /**
   * 'cover'   → Bild fuellt die Box, Seiten werden ggf. beschnitten
   *             (Default — sinnvoll fuer quadratische Grid-Thumbnails).
   * 'contain' → Bild bleibt vollstaendig sichtbar mit eigenem
   *             Seitenverhaeltnis (Lightbox).
   */
  fit?: 'cover' | 'contain';
}

/**
 * `<img>` für ein authentifiziertes API-Endpoint (/photos/:id/file).
 * Ein normales `<img src=…>` schickt keinen Bearer-Token, deshalb
 * holen wir das Bild via axios-Instanz (die den Token-Interceptor hat)
 * als Blob und bauen daraus eine object URL.
 *
 * Lazy: IntersectionObserver, lädt erst wenn ~200 px vom Viewport
 * entfernt. So bleiben 30+ Fotos in einer Galerie performant.
 */
export function AuthedImage({
  photoId,
  alt,
  style,
  className,
  onClick,
  eager = false,
  fit = 'cover',
}: AuthedImageProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(eager);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (eager || visible) return;
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [eager, visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    api
      .get(`/photos/${photoId}/file`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId, visible]);

  const isContain = fit === 'contain';

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        background: isContain ? 'transparent' : 'var(--color-surface-alt)',
        position: 'relative',
        overflow: 'hidden',
        display: isContain ? 'flex' : undefined,
        alignItems: isContain ? 'center' : undefined,
        justifyContent: isContain ? 'center' : undefined,
        ...style,
      }}
      onClick={onClick}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          style={
            isContain
              ? {
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }
              : { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
          }
        />
      ) : failed ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-soft)', fontSize: 12 }}>
          Bild nicht ladbar
        </div>
      ) : (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-soft)', fontSize: 12 }}>
          …
        </div>
      )}
    </div>
  );
}
